import { useState } from 'react';
import { useDraft } from '../contexts/DraftContext';

// Display order for position slots (BN excluded — handled separately as bench)
const POSITION_ORDER = ['QB', 'RB', 'WR', 'TE', 'FLEX', 'K', 'DEF'];

// Fallback for drafts created before rosterConfig was persisted
const DEFAULT_ROSTER = [
  { pos: 'QB',  slots: 1 },
  { pos: 'RB',  slots: 2 },
  { pos: 'WR',  slots: 2 },
  { pos: 'TE',  slots: 1 },
  { pos: 'K',   slots: 1 },
  { pos: 'DEF', slots: 1 },
];

function buildRoster(rosterConfig) {
  if (!rosterConfig) return DEFAULT_ROSTER;
  return POSITION_ORDER
    .filter(pos => (rosterConfig[pos] ?? 0) > 0)
    .map(pos => ({ pos, slots: rosterConfig[pos] }));
}

/**
 * Shows the roster for a selected team, grouped by position with slot-fill indicators.
 * Picks beyond a position's cap and picks for unconfigured positions appear in a BN section.
 * Team tabs let the user view any team's picks.
 */
function TeamRoster() {
  const { draft, picks, draftTeams, mySlotIndex } = useDraft();
  const [selectedTeam, setSelectedTeam] = useState(null); // null = user's team

  if (!draft) return null;

  const ownerSlotIndex = draftTeams.find(t => t.isOwnerSlot)?.slotIndex ?? 0;
  // See DraftBoard's identical comment: isOwnerSlot is always the commissioner's slot, not
  // "whoever is viewing" -- Commissioner Mode needs the per-viewer mySlotIndex instead.
  const myTeamSlotIndex = draft.commissionerMode ? mySlotIndex : ownerSlotIndex;
  const effectiveTeam = selectedTeam !== null ? selectedTeam : myTeamSlotIndex;

  const teamPicks = picks.filter(p => p.slotIndex === effectiveTeam);
  const roster = buildRoster(draft.rosterConfig);

  // Group picks by position
  const byPos = {};
  teamPicks.forEach(p => {
    if (!byPos[p.position]) byPos[p.position] = [];
    byPos[p.position].push(p);
  });

  // Starters: first N picks per configured position
  // Bench: overflow beyond cap + picks for positions not in the config
  const slotPositions = new Set(roster.map(r => r.pos));
  const benchPicks = [];

  roster.forEach(({ pos, slots }) => {
    const filled = byPos[pos] || [];
    if (filled.length > slots) {
      benchPicks.push(...filled.slice(slots));
    }
  });

  // Picks for positions absent from rosterConfig also go to bench
  teamPicks.forEach(p => {
    if (!slotPositions.has(p.position)) {
      benchPicks.push(p);
    }
  });

  return (
    <div className='team-roster'>
      <h3 className='panel-title'>Team Roster</h3>

      {/* Team selector tabs */}
      <div className='filter-group roster-tabs'>
        {draftTeams.map((team) => (
          <button
            key={team.slotIndex}
            className={[
              'filter-pill',
              team.slotIndex === effectiveTeam ? 'active' : '',
              team.slotIndex === myTeamSlotIndex ? 'my-team-pill' : '',
            ].filter(Boolean).join(' ')}
            onClick={() => setSelectedTeam(team.slotIndex)}
            title={team.teamName}
          >
            {team.teamName.length > 10 ? team.teamName.slice(0, 9) + '…' : team.teamName}
          </button>
        ))}
      </div>

      <div className='roster-grid'>
        {roster.map(({ pos, slots }) => {
          const filled = (byPos[pos] || []).slice(0, slots);
          const isFull = filled.length >= slots;
          return (
            <div key={pos} className='roster-pos-group'>
              <div className='roster-pos-header'>
                <span className={`pos-badge pos-${pos}`}>{pos}</span>
                <span className={`roster-fill-count ${isFull ? 'full' : ''}`}>
                  {filled.length}/{slots}
                </span>
              </div>
              {Array.from({ length: slots }, (_, i) => (
                <div key={i} className='roster-slot'>
                  {filled[i] ? (
                    <>
                      <span className='roster-player-name'>{filled[i].playerName}</span>
                      <span className='roster-player-team'>{filled[i].team}</span>
                    </>
                  ) : (
                    <span className='roster-empty-slot'>Empty</span>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        {benchPicks.length > 0 && (
          <div className='roster-pos-group'>
            <div className='roster-pos-header'>
              <span className='pos-badge pos-BN'>BN</span>
            </div>
            {benchPicks.map((p, i) => (
              <div key={i} className='roster-slot'>
                <span className='roster-player-name'>{p.playerName}</span>
                <span className='roster-player-team'>{p.team}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TeamRoster;
